import {
  ERROR_CODES,
  type PaginatedResult,
  type PanelCreateOrganizationInput,
  type PanelEventReportListDto,
  type PanelEventSurveyStateDto,
  type PanelPostEventListDto,
  type PanelOrganizationDetailDto,
  type PanelOrganizationListQuery,
  type PanelOrganizationRowDto,
  type PanelOwnerEmailCheckDto,
  type PanelOwnerInvitationDto,
  type PanelProvisionedOrganizationDto,
} from '@icebreaker/shared';
import { normalizeApiError } from '@icebreaker/shared/store';
import { api } from '@/shared/api/client';

/** Kurumlar `api` katmanı — YALNIZCA sagadan çağrılır. */
export async function fetchOrganizations(
  query: PanelOrganizationListQuery,
): Promise<PaginatedResult<PanelOrganizationRowDto>> {
  return await api.panelOrganizations.list({
    q: query.q,
    page: query.page,
    pageSize: query.pageSize,
  });
}

export async function fetchOrganization(id: string): Promise<PanelOrganizationDetailDto> {
  return await api.panelOrganizations.get(id);
}

/** Kurumun bitmiş etkinliklerinin rapor özeti (organizatör raporu Story 5). */
export async function fetchOrganizationReports(
  organizationId: string,
): Promise<PanelEventReportListDto> {
  return await api.panelOrganizations.eventReports(organizationId);
}

/** Kurumun bitmiş etkinliklerinde belge ve anket durumu (belge ve anket Story 8). */
export async function fetchOrganizationPostEvent(
  organizationId: string,
): Promise<PanelPostEventListDto> {
  return await api.panelOrganizations.postEvent(organizationId);
}

/** "Anketi kapat" (belge ve anket Story 8): gerekçe zorunlu, ikinci kapatma 409. */
export async function disableEventSurvey(arg: {
  eventId: string;
  reason: string;
}): Promise<PanelEventSurveyStateDto> {
  return await api.panelOrganizations.disableEventSurvey(arg.eventId, { reason: arg.reason });
}

export async function provisionOrganization(
  input: PanelCreateOrganizationInput,
): Promise<PanelProvisionedOrganizationDto> {
  return await api.panelOrganizations.provision(input);
}

/** TP1 ön kontrolü: sahip adresi bir kişisel hesaba mı kayıtlı. */
export async function checkOwnerEmail(ownerEmail: string): Promise<PanelOwnerEmailCheckDto> {
  return await api.panelOrganizations.checkOwnerEmail({ ownerEmail });
}

/** Kurum kapağını gerekçeyle kaldırır (Story P1); yanıt güncel detaydır. */
export async function removeOrganizationCover(input: {
  readonly organizationId: string;
  readonly coverMediaId: string;
  readonly reason: string;
}): Promise<PanelOrganizationDetailDto> {
  return await api.panelOrganizations.removeCover(input.organizationId, {
    coverMediaId: input.coverMediaId,
    reason: input.reason,
  });
}

export async function resendOwnerInvitation(
  organizationId: string,
): Promise<PanelOwnerInvitationDto> {
  return await api.panelOrganizations.resendOwnerInvitation(organizationId);
}

/**
 * SAHİP DAVETİNİ YENİ ADRESE KUR. `revokeFirst` iken önce bekleyen davet
 * iptal edilir, sonra yeni davet gönderilir. İki adım ayrı uçtur: iptal
 * başarılı olup davet düşerse kurum SAHİPSİZ kalır ve ekran bunu dürüstçe
 * gösterir (sahip bloğu "etkin sahibi yok" hâline geçer, "Yeni sahip daveti
 * gönder" eylemi açılır). Yeniden deneme bu yüzden yalnız ikinci adımı koşar.
 */
export async function replaceOwnerInvitation(input: {
  readonly organizationId: string;
  readonly ownerEmail: string;
  readonly revokeFirst: boolean;
}): Promise<PanelOwnerInvitationDto> {
  if (input.revokeFirst) {
    try {
      await api.panelOrganizations.revokeOwnerInvitation(input.organizationId);
    } catch (error) {
      // Bekleyen davet yok: önceki, sonucu doğrulanamayan deneme iptali zaten
      // yapmış olabilir. Hedef hâl (bekleyen davet yok) sağlandığı için ikinci
      // adıma geçilir, personel kendi iptaline takılmaz.
      if (normalizeApiError(error).code !== ERROR_CODES.ORGANIZATION_OWNER_INVITATION_CONFLICT) {
        throw error;
      }
    }
  }
  return await api.panelOrganizations.inviteOwner(input.organizationId, {
    ownerEmail: input.ownerEmail,
  });
}
